<script setup>
import {useCalcShipping} from "@/services/calcShipping";
import {onMounted} from "vue";
import { me } from "@/services/api";
import LogoBlack from "../assets/img1.jpg";

const {
  getAuctions,
  selectedAuction,
  selectedLocation,
  auctions,
  ports,
  locations,
  location,
  loading,
  title,
  checkTitleValue,
  checkTitle,
  calcShipping,
  getPorts,
  getCities,
} = useCalcShipping();

const hasAnyPrice = () => {
  return ports.value.some(port => location.value['port' + port.id] > 0);
};

onMounted(async () => {
  await me();
  await getAuctions();
  await getPorts();
});
</script>

<template>
  <!-- FULL SCREEN CENTERED -->
  <div class="w-full h-screen flex items-center justify-center p-4">
    <PrimeCard class="w-full max-w-[1100px]">
      <template #content>
        <div class="flex flex-col sm:flex-row gap-6">
          <!-- LEFT: LOGO -->
          <div class="w-full sm:w-[250px] flex justify-center items-start">
            <img :src="LogoBlack" alt="Logo" class="w-full object-cover h-full" />
          </div>

          <!-- RIGHT: TABS -->
          <div class="w-full flex-1 min-h-[260px] flex flex-col justify-between">
            <TabView>
              <!-- TAB 1: SHIPPING -->
              <TabPanel header="Shipping Calculator">
                <div class="flex flex-col sm:flex-row gap-6 items-start">
                  <!-- LEFT: Form -->
                  <div class="w-full sm:w-1/2 flex flex-col gap-4">
                    <PrimeDropdown
                        class="w-full"
                        size="large"
                        :filter="true"
                        :show-clear="true"
                        v-model="selectedAuction"
                        :options="auctions"
                        optionLabel="title"
                        option-value="id"
                        @update:model-value="getCities"
                        :loading="loading.auction"
                        placeholder="Select Auction"
                    />

                    <PrimeDropdown
                        class="w-full"
                        size="large"
                        :filter="true"
                        :show-clear="true"
                        v-model="selectedLocation"
                        :options="locations"
                        optionLabel="label"
                        option-value="id"
                        :loading="loading.location"
                        placeholder="Select Location"
                    />

                    <PrimeButton
                        :loading="loading.calculation"
                        label="Get Price"
                        @click="calcShipping"
                        icon="pi pi-dollar"
                        class="w-full"
                    />
                  </div>

                  <!-- RIGHT: Result -->
                  <div class="w-full sm:w-1/2">
                    <h3 class="font-semibold text-gray-700 mb-2">Shipping Price</h3>
                    <div class="w-full border-t pt-4">
                      <div v-if="Object.keys(location).length > 0">
                        <div v-if="hasAnyPrice()" class="space-y-2">
                          <div v-for="port in ports" :key="port.id">
                            <div v-if="location['port' + port.id] > 0"
                                 class="flex justify-between items-center border-b pb-1 text-sm">
                              <span class="text-gray-700 font-medium">Price ({{ port.title }})</span>
                              <span class="text-blue-600 font-semibold">${{ location['port' + port.id] + 30 + 100 }}</span>
                            </div>
                          </div>
                        </div>
                        <div v-else class="text-red-500 font-semibold mt-2 text-sm">
                          Price: $0 (ask company)
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabPanel>

              <!-- TAB 2: TITLE CHECK -->
              <TabPanel header="Title Check">
                <div class="flex flex-col sm:flex-row gap-6 items-start">
                  <!-- LEFT: Info -->
                  <div class="w-full sm:w-1/2 text-sm text-gray-800">
                    <p class="mb-2">
                      <strong>Supported format:</strong><br>
                      <span class="text-blue-600">CERT OF TITLE-SALVAGE</span>
                    </p>
                    <p>
                      <strong>Not supported:</strong><br>
                      <span class="text-red-600">GA - CERT OF TITLE-SALVAGE</span>
                    </p>
                  </div>

                  <!-- RIGHT: Title Check Form -->
                  <div class="w-full sm:w-1/2 flex flex-col gap-3">
                    <PrimeInputText
                        v-model="title"
                        placeholder="Enter Title"
                        class="w-full"
                    />

                    <div class="title-block w-full" v-if="checkTitleValue">
                      <div class="bg-gray-100 border p-2 rounded text-center text-sm text-gray-700">
                        {{ checkTitleValue }}
                      </div>
                    </div>

                    <PrimeButton
                        :loading="loading.titleCheck"
                        label="Check Title"
                        icon="pi pi-search"
                        @click="checkTitle"
                        class="w-full"
                    />
                  </div>
                </div>
              </TabPanel>
            </TabView>
          </div>
        </div>
      </template>
    </PrimeCard>
  </div>
</template>
